import { theme } from 'antd';

export const COLOR_SCHEME_CATEGORIES = [
  { id: 'natural', label: 'Natural & Professional' },
  { id: 'saas', label: 'Modern SaaS / Enterprise' },
  { id: 'fresh', label: 'Fresh & Friendly' },
  { id: 'premium', label: 'Premium / Executive' },
  { id: 'classic', label: 'Classic & Vibrant' },
];

const TEXT_DARK = '#1a2332';
const TEXT_LIGHT = '#ffffff';

function buildScheme(
  label,
  category,
  primary,
  primaryHover,
  primaryActive,
  lightLayout,
  lightSurface,
  darkLayout = '#141414',
  darkSurface = '#1f1f1f',
  options = {}
) {
  return {
    label,
    category,
    primary,
    primaryHover,
    primaryActive,
    sidebarVariant: options.sidebarVariant === 'light' ? 'light' : 'dark',
    preview: {
      light: { layout: lightLayout, surface: lightSurface, text: TEXT_DARK },
      dark: { layout: darkLayout, surface: darkSurface, text: TEXT_LIGHT },
    },
  };
}

/** Preset color combinations — add or edit schemes here. */
export const COLOR_SCHEMES = {
  'pasture-green': buildScheme(
    'Pasture Green',
    'natural',
    '#3d9a68',
    '#35925f',
    '#2d7a52',
    '#f5f0e8',
    '#fffef9',
    '#101612',
    '#1a211c'
  ),
  meadow: buildScheme('Meadow', 'natural', '#276749', '#2f855a', '#22543d', '#e8ebe4', '#f7f5f0', '#101612', '#1a211c'),
  'green-pastures': buildScheme('Green Pastures', 'natural', '#059669', '#10b981', '#047857', '#ecfdf5', '#fffff8', '#0f1612', '#1a211c'),
  'earth-field': buildScheme('Earth & Field', 'natural', '#6b705c', '#7c8268', '#565949', '#f0ebe3', '#faf8f5', '#141310', '#1f1e1a'),
  harvest: buildScheme('Harvest', 'natural', '#4a7c59', '#5a9368', '#3d6349', '#fef9e7', '#fffef7', '#121408', '#1a2110'),
  farmstead: buildScheme('Farmstead', 'natural', '#556b2f', '#6b8328', '#434f24', '#f5efe6', '#fefcf8', '#121408', '#1a1f10'),
  countryside: buildScheme('Countryside', 'natural', '#87a878', '#98b889', '#728a65', '#f4f1ea', '#fffdf7', '#121408', '#1a1f12'),
  prairie: buildScheme('Prairie', 'natural', '#1e4d3a', '#276749', '#163d2e', '#f5ead6', '#faf6ee', '#0f1612', '#1a2118'),
  agricore: buildScheme('AgriCore', 'saas', '#1b4332', '#276749', '#142f24', '#e8ecef', '#ffffff', '#0f1412', '#1a211c'),
  'livestock-pro': buildScheme('Livestock Pro', 'saas', '#22863a', '#2ea043', '#1a6b2e', '#eef0f2', '#ffffff', '#101612', '#1a211c'),
  ranchtech: buildScheme('RanchTech', 'saas', '#3d8b6e', '#4a9d7f', '#2f7359', '#edf2f7', '#fafbfc', '#0f1418', '#1a2128'),
  agriflow: buildScheme('AgriFlow', 'saas', '#0d9488', '#14b8a6', '#0f766e', '#f1f5f4', '#ffffff', '#0f1616', '#1a2220'),
  farmos: buildScheme('FarmOS', 'saas', '#166534', '#15803d', '#14532d', '#f7fee7', '#ffffff', '#0f1610', '#1a2114'),
  terra: buildScheme('Terra', 'saas', '#4d6a4f', '#5c7d5e', '#3f5841', '#ebe6dc', '#f8f6f2', '#141310', '#1f1e1a'),
  fieldops: buildScheme('FieldOps', 'saas', '#3d7a5f', '#4a9070', '#2f6249', '#e8ecf0', '#ffffff', '#0f1418', '#1a2124'),
  'white-sidebar': buildScheme(
    'White Sidebar',
    'saas',
    '#3d9a68',
    '#35925f',
    '#2d7a52',
    '#f5f7fa',
    '#ffffff',
    '#141414',
    '#1f1f1f',
    { sidebarVariant: 'light' }
  ),
  'fresh-pasture': buildScheme('Fresh Pasture', 'fresh', '#2d9a6f', '#3db384', '#247a58', '#ecfdf5', '#fefcf9', '#0f1612', '#1a211c'),
  'spring-meadow': buildScheme('Spring Meadow', 'fresh', '#65a30d', '#84cc16', '#4d7c0f', '#fefce8', '#fffff8', '#121408', '#1a2110'),
  greenfield: buildScheme('Greenfield', 'fresh', '#10b981', '#34d399', '#059669', '#eef4ee', '#fefcf9', '#0f1612', '#1a211c'),
  willow: buildScheme('Willow', 'fresh', '#5a8f5a', '#6ba06b', '#4a784a', '#f0ebe3', '#faf8f5', '#121408', '#1a1f14'),
  clover: buildScheme('Clover', 'fresh', '#4a934a', '#57a657', '#3d7a3d', '#fefdf5', '#fffef8', '#101612', '#1a2114'),
  'heritage-ranch': buildScheme('Heritage Ranch', 'premium', '#1a3d2e', '#234d3a', '#142e22', '#f5f0e6', '#fffef9', '#0f1410', '#1a2118'),
  'golden-pasture': buildScheme('Golden Pasture', 'premium', '#2d5a3d', '#3a6b4c', '#234830', '#fef9e7', '#fffef7', '#101612', '#1a2114'),
  'oak-field': buildScheme('Oak & Field', 'premium', '#4a5d3f', '#586f4a', '#3d4d34', '#f5f0ea', '#fffff8', '#121408', '#1a1f12'),
  'ranch-heritage': buildScheme('Ranch Heritage', 'premium', '#355834', '#426842', '#2a4729', '#f5efe6', '#fffdf7', '#101610', '#1a2114'),
  'estate-green': buildScheme('Estate Green', 'premium', '#1e4620', '#275929', '#163419', '#f5f3eb', '#fdfcf8', '#0f1410', '#1a2114'),
  ocean: buildScheme('Ocean Blue', 'classic', '#1890ff', '#4096ff', '#0958d9', '#f0f7ff', '#ffffff', '#0f1419', '#1a2332'),
  'deep-forest': buildScheme('Deep Forest', 'classic', '#237804', '#389e0d', '#135200', '#f6ffed', '#ffffff', '#141414', '#1f1f1f'),
  amber: buildScheme('Harvest Amber', 'classic', '#d48806', '#faad14', '#ad6800', '#fffbe6', '#ffffff', '#141414', '#1f1f1f'),
  grape: buildScheme('Royal Grape', 'classic', '#722ed1', '#9254de', '#531dab', '#f9f0ff', '#ffffff', '#141414', '#1f1f1f'),
  crimson: buildScheme('Crimson Red', 'classic', '#cf1322', '#f5222d', '#a8071a', '#fff1f0', '#ffffff', '#141414', '#1f1f1f'),
  rose: buildScheme('Rose Pink', 'classic', '#eb2f96', '#f759ab', '#c41d7f', '#fff0f6', '#ffffff', '#141414', '#1f1f1f'),
  teal: buildScheme('Clear Teal', 'classic', '#13c2c2', '#36cfc9', '#08979c', '#e6fffb', '#ffffff', '#141414', '#1f1f1f'),
  indigo: buildScheme('Indigo Blue', 'classic', '#2f54eb', '#597ef7', '#1d39c4', '#f0f5ff', '#ffffff', '#141414', '#1f1f1f'),
  copper: buildScheme('Copper Rust', 'classic', '#d4380d', '#fa541c', '#ad2102', '#fff2e8', '#ffffff', '#141414', '#1f1f1f'),
  emerald: buildScheme('Bright Emerald', 'classic', '#10b981', '#34d399', '#059669', '#ecfdf5', '#ffffff', '#141414', '#1f1f1f'),
  sapphire: buildScheme('Sapphire', 'classic', '#096dd9', '#1890ff', '#0050b3', '#e6f4ff', '#ffffff', '#141414', '#1f1f1f'),
  violet: buildScheme('Soft Violet', 'classic', '#7c3aed', '#8b5cf6', '#6d28d9', '#f5f3ff', '#ffffff', '#141414', '#1f1f1f'),
  lime: buildScheme('Fresh Lime', 'classic', '#84cc16', '#a3e635', '#65a30d', '#f7fee7', '#ffffff', '#141414', '#1f1f1f'),
  sunset: buildScheme('Sunset Blaze', 'classic', '#ea580c', '#f97316', '#c2410c', '#fff7ed', '#ffffff', '#1a120e', '#241912'),
  burgundy: buildScheme('Velvet Wine', 'classic', '#9f1239', '#be123c', '#881337', '#fff1f2', '#ffffff', '#170c10', '#211018'),
  gold: buildScheme('Saffron Gold', 'classic', '#ca8a04', '#eab308', '#a16207', '#fefce8', '#ffffff', '#17140a', '#211e10'),
  espresso: buildScheme('Mocha Earth', 'classic', '#78350f', '#92400e', '#5c2a0a', '#fef3c7', '#ffffff', '#15100c', '#1f1812'),
  olive: buildScheme('Pasture Olive', 'classic', '#65801c', '#7c9424', '#556618', '#f4f7e8', '#ffffff', '#121408', '#1a1f0f'),
  steel: buildScheme('Slate Steel', 'classic', '#475569', '#64748b', '#334155', '#f1f5f9', '#ffffff', '#111318', '#1a1d24'),
  fuchsia: buildScheme('Orchid Bright', 'classic', '#c026d3', '#d946ef', '#a21caf', '#fdf4ff', '#ffffff', '#160c18', '#201024'),
  peacock: buildScheme('Lagoon Peacock', 'classic', '#0e7490', '#0891b2', '#155e75', '#ecfeff', '#ffffff', '#0a1418', '#0f1c22'),
  arctic: buildScheme('Arctic Sky', 'classic', '#0284c7', '#0ea5e9', '#0369a1', '#f0f9ff', '#ffffff', '#0a1218', '#101c28'),
  sand: buildScheme('Desert Sand', 'classic', '#b45309', '#d97706', '#92400e', '#fffbeb', '#ffffff', '#18120a', '#221a0f'),
  mulberry: buildScheme('Mulberry Dusk', 'classic', '#7e22ce', '#9333ea', '#6b21a8', '#faf5ff', '#ffffff', '#120a18', '#1a1024'),
};

export const DEFAULT_COLOR_SCHEME = 'pasture-green';

const LEGACY_SCHEME_ALIASES = {
  maveshi: 'pasture-green',
  forest: 'deep-forest',
};

export function resolveColorScheme(schemeId) {
  const normalized = LEGACY_SCHEME_ALIASES[schemeId] || schemeId;
  if (normalized && COLOR_SCHEMES[normalized]) {
    return normalized;
  }
  return DEFAULT_COLOR_SCHEME;
}

export const COLOR_SCHEME_GROUPS = COLOR_SCHEME_CATEGORIES.map((category) => ({
  ...category,
  schemes: Object.entries(COLOR_SCHEMES)
    .filter(([, scheme]) => scheme.category === category.id)
    .map(([id, scheme]) => ({
      id,
      ...scheme,
      isDefault: id === DEFAULT_COLOR_SCHEME,
    })),
}));

export const COLOR_SCHEME_OPTIONS = COLOR_SCHEME_GROUPS.flatMap((group) => group.schemes);

/** @deprecated Use COLOR_SCHEMES['pasture-green'] — kept for existing imports */
export const MAVESHI_BRAND = COLOR_SCHEMES[DEFAULT_COLOR_SCHEME];

const FONT_FAMILY = `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
  'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif`;

const densityTokens = {
  fontSize: 13,
  fontSizeSM: 12,
  fontSizeLG: 15,
  fontSizeXL: 18,
  fontSizeHeading1: 28,
  fontSizeHeading2: 22,
  fontSizeHeading3: 18,
  fontSizeHeading4: 15,
  fontSizeHeading5: 13,
  controlHeight: 36,
  controlHeightSM: 28,
  controlHeightLG: 40,
  padding: 12,
  paddingSM: 8,
  paddingLG: 16,
  paddingXS: 4,
  margin: 12,
  marginSM: 8,
  marginLG: 16,
  marginXS: 4,
  borderRadius: 6,
  borderRadiusSM: 4,
  borderRadiusLG: 8,
  lineHeight: 1.45,
  fontWeightStrong: 500,
};

const compactTokens = {
  fontSize: 12,
  fontSizeSM: 11,
  fontSizeLG: 14,
  fontSizeXL: 16,
  fontSizeHeading1: 24,
  fontSizeHeading2: 20,
  fontSizeHeading3: 16,
  fontSizeHeading4: 14,
  fontSizeHeading5: 12,
  controlHeight: 32,
  controlHeightSM: 26,
  controlHeightLG: 38,
  padding: 10,
  paddingSM: 6,
  paddingLG: 14,
  paddingXS: 4,
  margin: 10,
  marginSM: 6,
  marginLG: 14,
  marginXS: 4,
  borderRadius: 6,
  borderRadiusSM: 4,
  borderRadiusLG: 8,
  lineHeight: 1.4,
};

const lightSurfaceTokens = {
  colorBgLayout: '#f5f7fa',
  colorBgContainer: '#ffffff',
  colorBgElevated: '#ffffff',
  colorText: '#1a2332',
  colorTextSecondary: '#64748b',
  colorTextTertiary: '#94a3b8',
  colorBorder: 'rgba(15, 20, 25, 0.09)',
  colorBorderSecondary: 'rgba(15, 20, 25, 0.08)',
  colorFillSecondary: 'rgba(15, 20, 25, 0.04)',
  colorFillTertiary: 'rgba(15, 20, 25, 0.03)',
};

const darkSurfaceTokens = {
  colorBgLayout: '#141414',
  colorBgContainer: '#1f1f1f',
  colorBgElevated: '#121820',
  colorText: 'rgba(255, 255, 255, 0.92)',
  colorTextSecondary: 'rgba(255, 255, 255, 0.52)',
  colorTextTertiary: 'rgba(255, 255, 255, 0.38)',
  colorBorder: 'rgba(255, 255, 255, 0.08)',
  colorBorderSecondary: 'rgba(255, 255, 255, 0.08)',
  colorFillSecondary: 'rgba(255, 255, 255, 0.06)',
  colorFillTertiary: 'rgba(255, 255, 255, 0.04)',
  colorBgContainerDisabled: '#333333',
  colorTextDisabled: '#777777',
};

export function getColorScheme(schemeId = DEFAULT_COLOR_SCHEME) {
  return COLOR_SCHEMES[schemeId] || COLOR_SCHEMES[DEFAULT_COLOR_SCHEME];
}

export function getSidebarVariant(colorSchemeId = DEFAULT_COLOR_SCHEME) {
  return getColorScheme(colorSchemeId).sidebarVariant === 'light' ? 'light' : 'dark';
}

/** Dark sidebar shell colors tinted by the active color scheme primary. */
export function getSidebarSurfaceColors(colorSchemeId = DEFAULT_COLOR_SCHEME) {
  const brand = getColorScheme(colorSchemeId);

  if (brand.sidebarVariant === 'light') {
    return {
      bg: '#ffffff',
      bgElevated: '#fafbfc',
      bgDeep: '#f4f6f8',
      border: 'rgba(15, 20, 25, 0.09)',
      controlBg: 'rgba(15, 20, 25, 0.04)',
      controlBgHover: 'rgba(15, 20, 25, 0.06)',
      controlBorder: 'rgba(15, 20, 25, 0.1)',
      brandLogoBg: '#ffffff',
      menuSelectedBg: primaryAlpha(brand.primary, 0.1),
      menuSelectedBgHover: primaryAlpha(brand.primary, 0.14),
      text: '#1a2332',
      textMuted: '#64748b',
      textActive: '#0f172a',
      hover: 'rgba(15, 20, 25, 0.05)',
    };
  }

  return {
    bg: mixHex('#0f1419', brand.primary, 0.2),
    bgElevated: mixHex('#151c24', brand.primary, 0.16),
    bgDeep: mixHex('#0b1014', brand.primary, 0.14),
    border: primaryAlpha(brand.primary, 0.14),
    controlBg: primaryAlpha(brand.primary, 0.1),
    controlBgHover: primaryAlpha(brand.primary, 0.16),
    controlBorder: primaryAlpha(brand.primary, 0.2),
    brandLogoBg: mixHex('#151c24', brand.primary, 0.24),
    menuSelectedBg: mixHex('#121820', brand.primary, 0.24),
    menuSelectedBgHover: mixHex('#161e27', brand.primary, 0.3),
    text: 'rgba(255, 255, 255, 0.72)',
    textMuted: 'rgba(255, 255, 255, 0.45)',
    textActive: '#ffffff',
    hover: 'rgba(255, 255, 255, 0.06)',
  };
}

function hexToRgb(hex) {
  const normalized = hex.replace('#', '');
  const value = parseInt(normalized, 16);
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
}

function primaryAlpha(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function mixHex(baseHex, tintHex, tintWeight = 0.14) {
  const base = hexToRgb(baseHex);
  const tint = hexToRgb(tintHex);
  const weight = Math.min(Math.max(tintWeight, 0), 1);
  const mix = (baseChannel, tintChannel) =>
    Math.round(baseChannel * (1 - weight) + tintChannel * weight);

  const r = mix(base.r, tint.r);
  const g = mix(base.g, tint.g);
  const b = mix(base.b, tint.b);

  return `#${[r, g, b].map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
}

function buildColorTokens(themeMode, colorSchemeId) {
  const brand = getColorScheme(colorSchemeId);
  const surface = themeMode === 'dark' ? darkSurfaceTokens : lightSurfaceTokens;

  return {
    ...surface,
    colorPrimary: brand.primary,
    colorPrimaryHover: brand.primaryHover,
    colorPrimaryActive: brand.primaryActive,
    colorInfo: brand.primary,
    colorLink: brand.primary,
  };
}

const buildTableTokens = (themeMode, isCompact, primary) => ({
  cellPaddingBlock: isCompact ? 6 : 8,
  cellPaddingInline: isCompact ? 10 : 12,
  cellFontSize: isCompact ? 12 : 13,
  headerBg: themeMode === 'dark' ? '#121820' : '#f6f8fa',
  headerColor: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.52)' : '#64748b',
  headerSplitColor: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 20, 25, 0.09)',
  borderColor: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 20, 25, 0.09)',
  rowHoverBg: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : '#f3f6f9',
  rowSelectedBg: primaryAlpha(primary, themeMode === 'dark' ? 0.14 : 0.08),
  rowSelectedHoverBg: primaryAlpha(primary, themeMode === 'dark' ? 0.2 : 0.12),
  headerSortActiveBg: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : '#eef1f5',
  headerSortHoverBg: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : '#eef1f5',
  footerBg: themeMode === 'dark' ? '#121820' : '#f6f8fa',
  stickyScrollBarBg: themeMode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(15, 20, 25, 0.12)',
});

const globalComponentTokens = {
  Button: {
    fontWeight: 400,
    contentFontSize: 13,
    contentFontSizeSM: 12,
    contentFontSizeLG: 14,
    paddingInline: 12,
    paddingInlineSM: 8,
    paddingInlineLG: 16,
    controlHeight: 36,
    controlHeightSM: 28,
    controlHeightLG: 40,
  },
  Collapse: {
    headerPadding: '8px 12px',
    contentPadding: '8px 12px',
    fontSize: 13,
  },
  Card: {
    paddingLG: 16,
    headerHeight: 44,
    headerFontSize: 14,
  },
  Form: {
    itemMarginBottom: 14,
    labelFontSize: 13,
  },
  Tabs: {
    horizontalMargin: '0 0 12px 0',
    titleFontSize: 13,
  },
  Select: {
    optionFontSize: 14,
    optionHeight: 36,
    optionPadding: '8px 12px',
    optionLineHeight: 1.45,
  },
  Dropdown: {
    controlItemHeight: 36,
    paddingBlock: 6,
    fontSize: 14,
  },
  Cascader: {
    optionHeight: 36,
    controlItemWidth: 120,
  },
  TreeSelect: {
    titleHeight: 36,
  },
  DatePicker: {
    inputFontSize: 13,
    inputFontSizeLG: 15,
  },
  InputNumber: {
    inputFontSize: 13,
    inputFontSizeLG: 15,
  },
  Modal: {
    titleFontSize: 15,
    contentPadding: 16,
  },
  Drawer: {
    paddingLG: 16,
  },
  Tag: {
    fontSize: 12,
  },
  Segmented: {
    controlHeight: 28,
  },
  Menu: {
    itemHeight: 40,
    iconSize: 15,
    fontSize: 14,
    dropdownWidth: 200,
  },
  List: {
    itemPadding: '8px 12px',
  },
  Pagination: {
    itemSize: 28,
  },
  Typography: {
    titleMarginBottom: 0.4,
    titleMarginTop: 0.6,
  },
};

const compactComponentTokens = {
  Button: {
    fontWeight: 400,
    contentFontSize: 12,
    contentFontSizeSM: 11,
    paddingInline: 10,
    paddingInlineSM: 7,
    paddingInlineLG: 14,
    controlHeight: 28,
    controlHeightSM: 22,
  },
  Collapse: {
    headerPadding: '6px 10px',
    contentPadding: '6px 10px',
    fontSize: 12,
  },
  Card: {
    paddingLG: 14,
    headerHeight: 40,
    headerFontSize: 13,
  },
  Modal: {
    titleFontSize: 14,
    contentPadding: 14,
  },
  Form: {
    itemMarginBottom: 12,
    labelFontSize: 12,
  },
  Menu: {
    itemHeight: 36,
    iconSize: 14,
    fontSize: 13,
    subMenuItemBg: 'transparent',
  },
  Layout: {
    headerHeight: 56,
    triggerHeight: 40,
  },
  Tabs: {
    horizontalMargin: '0 0 10px 0',
    titleFontSize: 12,
  },
  Select: {
    optionFontSize: 13,
    optionHeight: 32,
    optionPadding: '6px 10px',
  },
  Dropdown: {
    controlItemHeight: 32,
    fontSize: 13,
  },
  Segmented: {
    controlHeight: 26,
  },
  Typography: {
    titleMarginBottom: 0.35,
    titleMarginTop: 0.5,
  },
};

const getAlgorithm = (themeMode, isCompact, isMobileComfort = false) => {
  const useCompact = isCompact && !isMobileComfort;
  if (themeMode === 'dark') {
    return useCompact
      ? [theme.darkAlgorithm, theme.compactAlgorithm]
      : theme.darkAlgorithm;
  }
  return useCompact ? theme.compactAlgorithm : theme.defaultAlgorithm;
};

/** Touch-friendly sizing on small viewports — overrides compact density. */
export const mobileComfortTokens = {
  fontSize: 15,
  fontSizeSM: 13,
  fontSizeLG: 16,
  fontSizeXL: 18,
  controlHeight: 44,
  controlHeightSM: 36,
  controlHeightLG: 48,
  padding: 14,
  paddingSM: 10,
  paddingLG: 18,
  paddingXS: 6,
  margin: 14,
  marginSM: 10,
  marginLG: 18,
  lineHeight: 1.5,
};

const mobileComfortComponentTokens = {
  Button: {
    contentFontSize: 15,
    contentFontSizeSM: 13,
    contentFontSizeLG: 16,
    paddingInline: 16,
    paddingInlineSM: 12,
    paddingInlineLG: 20,
    controlHeight: 44,
    controlHeightSM: 36,
    controlHeightLG: 48,
  },
  Form: {
    itemMarginBottom: 18,
    labelFontSize: 14,
    verticalLabelPadding: 4,
  },
  Input: {
    inputFontSize: 16,
    inputFontSizeLG: 16,
    paddingBlock: 10,
    paddingInline: 12,
  },
  Select: {
    optionFontSize: 15,
    optionHeight: 40,
  },
  DatePicker: {
    inputFontSize: 16,
    inputFontSizeLG: 16,
  },
  InputNumber: {
    inputFontSize: 16,
    inputFontSizeLG: 16,
  },
  Card: {
    paddingLG: 18,
    headerHeight: 48,
    headerFontSize: 15,
  },
  Modal: {
    titleFontSize: 17,
    contentPadding: 18,
  },
  Drawer: {
    paddingLG: 18,
  },
  Tabs: {
    titleFontSize: 14,
    horizontalMargin: '0 0 14px 0',
  },
  Segmented: {
    controlHeight: 40,
  },
  Pagination: {
    itemSize: 36,
  },
};

/** Central Ant Design theme — palette from COLOR_SCHEMES. */
export function getMaveshiTheme(
  themeMode = 'light',
  isCompact = false,
  colorScheme = DEFAULT_COLOR_SCHEME,
  isMobileComfort = false
) {
  const brand = getColorScheme(colorScheme);
  const colorTokens = buildColorTokens(themeMode, colorScheme);
  const useCompact = isCompact && !isMobileComfort;

  return {
    cssVar: {
      key: 'maveshi',
    },
    algorithm: getAlgorithm(themeMode, isCompact, isMobileComfort),
    token: {
      fontFamily: FONT_FAMILY,
      ...colorTokens,
      ...densityTokens,
      ...(useCompact ? compactTokens : {}),
      ...(isMobileComfort ? mobileComfortTokens : {}),
    },
    components: {
      Table: buildTableTokens(themeMode, useCompact, brand.primary),
      ...globalComponentTokens,
      ...(useCompact ? compactComponentTokens : {}),
      ...(isMobileComfort ? mobileComfortComponentTokens : {}),
    },
  };
}

/** Read resolved tokens outside React (charts, utilities, tests). */
export function getMaveshiDesignToken(
  themeMode = 'light',
  isCompact = false,
  colorScheme = DEFAULT_COLOR_SCHEME,
  isMobileComfort = false
) {
  return theme.getDesignToken(getMaveshiTheme(themeMode, isCompact, colorScheme, isMobileComfort));
}
